import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Image, ImageType } from '../types/vehicle.types';

/**
 * Gestion des photos d'un véhicule (galerie). Tous les endpoints {@code /me}
 * de {@code /api/vehicles/{vehicleId}/photos} valident côté back que le
 * véhicule appartient au user courant.
 *
 * Types d'images supportés (cf. {@code ImageType} back) :
 *   - GENERAL                — photos générales (les seules visibles au public)
 *   - REGISTRATION_CARD      — carte grise
 *   - INSURANCE              — assurance
 *   - TECHNICAL_INSPECTION   — visite technique
 *
 * Upload : multipart/form-data avec un ou plusieurs fichiers + `type` en
 * query param (défaut `GENERAL`).
 */
@Injectable({ providedIn: 'root' })
export class VehiclePhotosService {
  private readonly http = inject(HttpClient);

  private base(vehicleId: number): string {
    return `${environment.gatewayUrl}/rental/api/vehicles/${vehicleId}/photos`;
  }

  /** Liste self-service des photos d'un véhicule, filtrée par type optionnel. */
  listMine(vehicleId: number, type?: ImageType): Observable<Image[]> {
    let params = new HttpParams();
    if (type) params = params.set('type', type);
    return this.http.get<Image[]>(`${this.base(vehicleId)}/me`, { params });
  }

  /** Upload une ou plusieurs photos. Returns les images persistées. */
  uploadMine(vehicleId: number, files: File[], type: ImageType = 'GENERAL'): Observable<Image[]> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    const params = new HttpParams().set('type', type);
    return this.http.post<Image[]>(`${this.base(vehicleId)}/me`, form, { params });
  }

  /** Change la catégorie (type) d'une photo existante. */
  updateTypeMine(vehicleId: number, photoId: number, type: ImageType): Observable<Image> {
    const params = new HttpParams().set('type', type);
    return this.http.patch<Image>(
      `${this.base(vehicleId)}/${photoId}/type/me`,
      null,
      { params },
    );
  }

  /** Supprime une photo. */
  deleteMine(vehicleId: number, photoId: number): Observable<void> {
    return this.http.delete<void>(`${this.base(vehicleId)}/${photoId}/me`);
  }

  /** Compteur de photos par type (limite éventuelle côté back). */
  countMine(vehicleId: number): Observable<Record<ImageType, number>> {
    return this.http.get<Record<ImageType, number>>(`${this.base(vehicleId)}/count/me`);
  }
}
